import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Eye, RotateCw, CheckCircle2, XCircle, MinusCircle, ChevronLeft, ChevronRight } from "lucide-react";

export const MAIL_STATUS = {
  sent: { label: "Envoyé", cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
  failed: { label: "Échoué", cls: "bg-red-100 text-red-700", icon: XCircle },
  skipped: { label: "Ignoré", cls: "bg-gray-100 text-gray-600", icon: MinusCircle },
};

const StatusBadge = ({ status, testid }) => {
  const s = MAIL_STATUS[status] || MAIL_STATUS.skipped;
  return <span data-testid={testid} className={`inline-flex items-center gap-1 text-xs px-2 py-1 ${s.cls}`}><s.icon className="w-3.5 h-3.5" /> {s.label}</span>;
};

const fmt = (d) => new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });

const LogPreview = ({ logId, onClose, onResend }) => {
  const [log, setLog] = useState(null);
  useEffect(() => { setLog(null); if (logId) api.get(`/admin/email-logs/${logId}`).then((r) => setLog(r.data)).catch(() => {}); }, [logId]);
  return (
    <Dialog open={!!logId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none max-h-[92vh] overflow-y-auto" data-testid="email-log-preview">
        <DialogHeader><DialogTitle className="font-display text-xl text-[#1B2A4A]">Aperçu de l'email reçu</DialogTitle></DialogHeader>
        {!log ? <p className="text-sm text-gray-400 py-8 text-center">Chargement…</p> : (
          <div className="space-y-4">
            <dl className="grid grid-cols-[110px_1fr] gap-y-1.5 text-sm bg-[#F8F6F0] border-l-4 border-[#E0C173] p-4">
              <dt className="text-[#475569]">Destinataire</dt><dd data-testid="preview-to" className="font-medium text-[#1B2A4A] break-all">{log.to || "— (aucune adresse)"}</dd>
              <dt className="text-[#475569]">Objet</dt><dd data-testid="preview-subject" className="font-medium text-[#1B2A4A]">{log.subject}</dd>
              <dt className="text-[#475569]">Modèle</dt><dd>{log.template_label}</dd>
              <dt className="text-[#475569]">Date</dt><dd>{fmt(log.created_at)}</dd>
              <dt className="text-[#475569]">Statut</dt><dd><StatusBadge status={log.status} testid="preview-status" /></dd>
              {log.error && <><dt className="text-[#475569]">Erreur</dt><dd data-testid="preview-error" className="text-red-600 break-words">{log.error}</dd></>}
            </dl>
            <iframe title="Aperçu email" data-testid="preview-iframe" srcDoc={log.html} sandbox="" className="w-full h-[480px] border border-gray-200 bg-white" />
            <div className="flex justify-end"><Button data-testid="preview-resend" onClick={() => onResend(log.log_id)} className="rounded-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white"><RotateCw className="w-4 h-4 mr-1.5" /> Renvoyer</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Filters = ({ q, setQ, status, setStatus, template, setTemplate, templates, counts }) => (
  <div className="flex flex-wrap gap-3 items-center">
    <div className="relative flex-1 min-w-[220px]">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <Input data-testid="email-logs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un destinataire ou un objet…" className="pl-9 rounded-none border-gray-300" />
    </div>
    <select data-testid="email-logs-status" value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 border border-gray-300 px-3 text-sm bg-white">
      <option value="">Tous les statuts</option>
      {Object.entries(MAIL_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label} ({counts?.[k] ?? 0})</option>)}
    </select>
    <select data-testid="email-logs-template" value={template} onChange={(e) => setTemplate(e.target.value)} className="h-10 border border-gray-300 px-3 text-sm bg-white max-w-[260px]">
      <option value="">Tous les types d'email</option>
      {templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
    </select>
  </div>
);

export default function EmailLogsPanel({ templates }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [template, setTemplate] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1, counts: {} });
  const [previewId, setPreviewId] = useState(null);

  const load = () => api.get("/admin/email-logs", { params: { q, status, template, page } }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, status, template, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => setPage(1), [q, status, template]);

  const resend = async (id) => {
    if (!window.confirm("Renvoyer cet email à son destinataire ?")) return;
    try {
      const { data: r } = await api.post(`/admin/email-logs/${id}/resend`);
      r.ok ? toast.success("Email renvoyé avec succès") : toast.error(`Non envoyé : ${r.error}`);
      setPreviewId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="email-logs-panel">
      <div className="grid grid-cols-3 gap-px bg-gray-200 border border-gray-200">
        {Object.entries(MAIL_STATUS).map(([k, v]) => (
          <button key={k} data-testid={`email-count-${k}`} onClick={() => setStatus(status === k ? "" : k)} className={`bg-white p-4 text-left transition-colors hover:bg-[#F8F6F0] ${status === k ? "ring-2 ring-inset ring-[#E0C173]" : ""}`}>
            <div className="font-display text-2xl text-[#1B2A4A]">{data.counts?.[k] ?? 0}</div>
            <div className="text-xs uppercase tracking-widest text-[#475569] flex items-center gap-1"><v.icon className="w-3.5 h-3.5" /> {v.label}s</div>
          </button>
        ))}
      </div>
      <Filters {...{ q, setQ, status, setStatus, template, setTemplate, templates, counts: data.counts }} />
      <div className="bg-white border border-gray-200 overflow-x-auto">
        {data.items.length === 0 ? <p className="text-gray-400 py-10 text-center text-sm" data-testid="email-logs-empty">Aucun email trouvé.</p> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-[#475569]">
              <tr><th className="p-3">Date</th><th className="p-3">Type</th><th className="p-3">Destinataire</th><th className="p-3">Objet</th><th className="p-3">Statut</th><th className="p-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {data.items.map((l) => (
                <tr key={l.log_id} data-testid={`email-log-${l.log_id}`} className="border-t border-gray-100 align-top">
                  <td className="p-3 whitespace-nowrap text-[#475569]">{fmt(l.created_at)}</td>
                  <td className="p-3 text-[#475569] min-w-[160px]">{l.template_label}{l.resent_from && <span className="ml-1 text-[10px] uppercase text-[#C9A85A]">renvoi</span>}</td>
                  <td className="p-3 text-[#1B2A4A] break-all">{l.to || "—"}</td>
                  <td className="p-3 text-[#1B2A4A] min-w-[220px]">{l.subject}{l.error && l.status === "failed" && <div className="text-xs text-red-600 mt-0.5">{l.error}</div>}</td>
                  <td className="p-3"><StatusBadge status={l.status} testid={`email-log-status-${l.log_id}`} /></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" data-testid={`email-log-view-${l.log_id}`} onClick={() => setPreviewId(l.log_id)} className="rounded-none"><Eye className="w-4 h-4 mr-1" /> Aperçu</Button>
                    <Button size="sm" variant="ghost" data-testid={`email-log-resend-${l.log_id}`} title="Renvoyer l'email" aria-label="Renvoyer l'email" onClick={() => resend(l.log_id)} className="rounded-none text-[#1B2A4A]"><RotateCw className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="flex items-center justify-between text-sm text-[#475569]">
        <span data-testid="email-logs-total">{data.total} email(s)</span>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="email-logs-prev" className="rounded-none"><ChevronLeft className="w-4 h-4" /></Button>
          <span>Page {data.page || page} / {data.pages}</span>
          <Button size="sm" variant="outline" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="email-logs-next" className="rounded-none"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      <LogPreview logId={previewId} onClose={() => setPreviewId(null)} onResend={resend} />
    </div>
  );
}
